import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useApi } from "../../api";
import { n2 } from "../../lib/format";
import { Modal, Notice } from "../../lib/ui";
import "./purchasing.css";

/*
 * Purchase order details – port of get_po_details.php + po_actions.php (API: /api/purchase-orders/<id>)
 * Route: /po/:id. While a PO is Pending its quantities and prices can be edited, and it can be
 * approved or rejected (rejecting deletes it).
 */

export default function PODetail() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [po, setPo] = useState(null);
  const [edits, setEdits] = useState({});
  const [notice, setNotice] = useState(location.state?.message ? { tone: "ok", text: location.state.message } : null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const d = await api.get(`/api/purchase-orders/${id}`); setPo(d); setEdits({}); }
    catch (e) { setError(e.message); }
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  const val = (it, k) => edits[it.item_id]?.[k] ?? String(it[k]);
  const setVal = (itemId, k, v) => setEdits((e) => ({ ...e, [itemId]: { ...e[itemId], [k]: v } }));
  const line = (it) => (parseFloat(val(it, "quantity")) || 0) * (parseFloat(val(it, "unit_price")) || 0);
  const grand = po ? po.items.reduce((s, it) => s + line(it), 0) : 0;
  const dirty = Object.keys(edits).length > 0;

  const act = async (action) => {
    setConfirm(null); setBusy(true); setNotice(null);
    try {
      const body = { action };
      if (action === "update") body.items = Object.fromEntries(po.items.map((it) => [it.item_id, { quantity: val(it, "quantity"), unit_price: val(it, "unit_price") }]));
      const r = await api.post(`/api/purchase-orders/${id}/action`, body);
      if (r.deleted) { navigate("/Approvedpo_list", { replace: true }); return; }
      setNotice({ tone: "ok", text: r.message });
      load();
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
    finally { setBusy(false); }
  };

  return (
    <main className="pg" style={{ maxWidth: "56rem" }}>
      <nav className="crumbs" aria-label="Breadcrumb"><Link to="/dashboard">Home</Link><i className="fas fa-chevron-right" />
        <Link to="/Approvedpo_list">Purchase orders</Link><i className="fas fa-chevron-right" /><span aria-current="page">PO #{id}</span></nav>
      <Notice notice={notice} onClose={() => setNotice(null)} />
      {error && <Notice notice={{ tone: "err", text: error }} />}
      {po && (
        <>
          <div className="pg-head">
            <div className="pg-title"><span className="chip"><i className="fas fa-file-contract text-orange-500" /></span>
              <div><h1>Purchase order #{po.po_id}</h1><p>{po.client.client_name} · {po.po_date}</p></div></div>
            <span className={`pill-tag ${po.status === "Pending" ? "warn" : String(po.status).toLowerCase() === "approved" ? "ok" : ""}`} style={{ fontSize: ".85rem" }}>{po.status}</span>
          </div>
          <section className="panel" style={{ padding: "1rem 1.25rem", marginBottom: "1rem" }}>
            <dl className="po-meta">
              <div><dt>Supplier</dt><dd>{po.client.client_name || "–"}</dd></div>
              <div><dt>Email</dt><dd>{po.client.contact_email || "–"}</dd></div>
              <div><dt>Phone</dt><dd>{po.client.contact_phone || "–"}</dd></div>
              <div><dt>PO date</dt><dd>{po.po_date}</dd></div>
              {po.approved_by && <div><dt>Approved by</dt><dd>{po.approved_by}{po.approval_date ? `, ${po.approval_date}` : ""}</dd></div>}
            </dl>
          </section>
          <section className="panel">
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>#</th><th>Product</th><th className="num">Quantity</th><th className="num">Unit price (TZS)</th><th className="num">Total (TZS)</th></tr></thead>
                <tbody>
                  {po.items.map((it, i) => (
                    <tr key={it.item_id}>
                      <td>{i + 1}</td><td style={{ fontWeight: 600, color: "var(--ink)" }}>{it.description}</td>
                      <td className="num">{po.editable ? <input className="cell" type="number" min="1" value={val(it, "quantity")} onChange={(e) => setVal(it.item_id, "quantity", e.target.value)} aria-label={`Quantity of ${it.description}`} style={{ maxWidth: "7rem", marginLeft: "auto" }} /> : <span className="mono">{it.quantity}</span>}</td>
                      <td className="num">{po.editable ? <input className="cell" type="number" min="0" step="0.01" value={val(it, "unit_price")} onChange={(e) => setVal(it.item_id, "unit_price", e.target.value)} aria-label={`Unit price of ${it.description}`} style={{ maxWidth: "9rem", marginLeft: "auto" }} /> : <span className="mono">{n2(it.unit_price)}</span>}</td>
                      <td className="num mono">{n2(line(it))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={4}>Total</td><td className="num mono">{n2(grand)}</td></tr></tfoot>
              </table>
              {po.items.length === 0 && <div className="empty">This order has no items.</div>}
            </div>
          </section>
          {po.editable && (
            <div style={{ display: "flex", justifyContent: "space-between", gap: ".6rem", marginTop: "1rem", flexWrap: "wrap" }}>
              <button type="button" className="btn" disabled={!dirty || busy} onClick={() => act("update")}><i className="fas fa-floppy-disk" /> Save changes</button>
              <span style={{ display: "flex", gap: ".6rem" }}>
                <button type="button" className="btn" disabled={busy} onClick={() => setConfirm("reject")}><i className="fas fa-xmark" /> Reject</button>
                <button type="button" className="btn primary" disabled={busy} onClick={() => setConfirm("approve")}><i className="fas fa-check" /> Approve</button>
              </span>
            </div>
          )}
        </>
      )}
      {confirm && (
        <Modal title={confirm === "approve" ? "Approve this purchase order?" : "Reject and delete this purchase order?"} onClose={() => setConfirm(null)}
          actions={<><button type="button" className="btn" onClick={() => setConfirm(null)}>Cancel</button>
            <button type="button" className={`btn ${confirm === "approve" ? "primary" : "danger"}`} onClick={() => act(confirm)}>{confirm === "approve" ? "Approve" : "Reject and delete"}</button></>}>
          {confirm === "approve"
            ? <>PO #{po.po_id} for TZS {n2(po.grand_total)} will be approved and can no longer be edited.{dirty && " Unsaved changes will be lost; save them first."}</>
            : <>PO #{po.po_id} and its items will be deleted. This can't be undone.</>}
        </Modal>
      )}
    </main>
  );
}
